import { useState } from "react";

import { Box, IconButton, Typography } from "@mui/material";
import { ArrowDropUp, ArrowDropDown, DragIndicator } from "@mui/icons-material";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";

import Delete from "../elements/Delete";
import EditorToolbar from "../editors/EditorToolbar";
import Input from "../elements/Input";

const Options = ({ value, label, onChange, onSave }) => {
  const { getLabel, getMessage } = useLocalization();
  const { newId } = useData();
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const moveOption = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= value.length ||
      toIndex >= value.length
    ) {
      return;
    }

    const reordered = [...value];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    onChange(reordered);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("dragType", "surveyOption");
    e.dataTransfer.setData("optionIndex", String(index));
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (e.dataTransfer.getData("dragType") !== "surveyOption") {
      return;
    }

    const sourceIndex = Number(e.dataTransfer.getData("optionIndex"));
    if (Number.isNaN(sourceIndex)) return;

    moveOption(sourceIndex, dropIndex);
  };

  const handleAdd = () => {
    let newValue = [...value, { id: newId(), name: "", name_fr: "" }];

    onChange(newValue);
  };

  const handleChange = (index, field, newValue) => {
    let newOptions = [];

    for (let x = 0; x < value.length; x++) {
      if (x !== index) {
        newOptions.push(value[x]);
        continue;
      }

      newOptions.push({ ...value[x], [field]: newValue });
    }

    onChange(newOptions);
  };

  const handleDelete = (index) => {
    let newOptions = [];
    for (let x = 0; x < value.length; x++) {
      if (x !== index) {
        newOptions.push(value[x]);
      }
    }

    onChange(newOptions);
  };

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      border="1px solid #ccc"
      borderRadius="5px"
    >
      {label ? <Typography align="center">{label}</Typography> : null}
      <EditorToolbar
        onAdd={handleAdd}
        onSave={onSave}
      />
      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        padding="10px"
      >
        {value.length > 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            paddingTop="5px"
          >
            {value.map((option, index) => (
              <Box
                key={option?._id || option?.id || `option-${index}`}
                display="flex"
                alignItems="center"
                gap={1}
                width="100%"
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setDragOverIndex(index)}
                onDragLeave={() => {
                  if (dragOverIndex === index) setDragOverIndex(null);
                }}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  border:
                    dragOverIndex === index
                      ? "1px dashed #f39c12"
                      : "1px dashed transparent",
                  borderRadius: "4px",
                  transition: "border-color 120ms ease",
                }}
              >
                <Box
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  sx={{ cursor: "grab", display: "flex", alignItems: "center" }}
                >
                  <DragIndicator fontSize="small" />
                </Box>

                <Input
                  label={getLabel("name")}
                  value={option.name}
                  onChange={(newValue) => handleChange(index, "name", newValue)}
                />

                <Input
                  label={getLabel("name_fr")}
                  value={option.name_fr}
                  onChange={(newValue) =>
                    handleChange(index, "name_fr", newValue)
                  }
                />

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <IconButton
                    size="small"
                    onClick={() => moveOption(index, index - 1)}
                    disabled={index === 0}
                    aria-label="Move option up"
                  >
                    <ArrowDropUp fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveOption(index, index + 1)}
                    disabled={index === value.length - 1}
                    aria-label="Move option down"
                  >
                    <ArrowDropDown fontSize="small" />
                  </IconButton>
                </Box>

                <Delete
                  id={index}
                  onDelete={handleDelete}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="overline">{getMessage("notSet")}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default Options;
